import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertContainerVariants = cva("flex rounded-md border", {
  variants: {
    dir: {
      horizontal: "flex-row items-center",
      vertical: "flex-col items-start",
    },
    variant: {
      default: "border-border bg-contrast/5",
      warning: "border-amber-500/25 bg-amber-500/10",
      destructive: "border-destructive/30 bg-destructive/10",
    },
    size: {
      sm: "gap-2 px-2.5 py-1.5",
      md: "gap-2.5 px-3 py-2",
      lg: "gap-3 px-4 py-3",
    },
  },
  defaultVariants: {
    dir: "horizontal",
    variant: "default",
    size: "md",
  },
})

type AlertContextValue = {
  dir: NonNullable<VariantProps<typeof alertContainerVariants>["dir"]>
  size: NonNullable<VariantProps<typeof alertContainerVariants>["size"]>
  variant: NonNullable<VariantProps<typeof alertContainerVariants>["variant"]>
}

const AlertContext = React.createContext<AlertContextValue | null>(null)

function useAlertContext(component: string): AlertContextValue {
  const ctx = React.useContext(AlertContext)
  if (!ctx) {
    throw new Error(`${component} must be used within Alert.Container`)
  }
  return ctx
}

const alertIconVariants = cva("shrink-0 [&_svg]:pointer-events-none", {
  variants: {
    size: {
      sm: "[&_svg:not([class*='size-'])]:size-3.5",
      md: "[&_svg:not([class*='size-'])]:size-4",
      lg: "[&_svg:not([class*='size-'])]:size-[18px]",
    },
    variant: {
      default: "text-contrast/80",
      warning: "text-amber-400",
      destructive: "text-destructive",
    },
  },
  defaultVariants: {
    size: "md",
    variant: "default",
  },
})

const alertTitleVariants = cva("font-semibold text-heading", {
  variants: {
    size: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base",
    },
  },
  defaultVariants: { size: "md" },
})

const alertContentVariants = cva("min-w-0 tracking-[-0.42px]", {
  variants: {
    size: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base",
    },
    variant: {
      default: "text-contrast/80",
      warning: "text-amber-100/90",
      destructive: "text-destructive",
    },
  },
  defaultVariants: {
    size: "md",
    variant: "default",
  },
})

export type AlertContainerProps = Omit<
  React.ComponentProps<"div">,
  "dir"
> &
  VariantProps<typeof alertContainerVariants>

function Container({
  className,
  dir: dirProp = "horizontal",
  variant: variantProp = "default",
  size: sizeProp = "md",
  ...props
}: AlertContainerProps) {
  const dir = dirProp ?? "horizontal"
  const variant = variantProp ?? "default"
  const size = sizeProp ?? "md"
  const resolved: AlertContextValue = { dir, size, variant }

  return (
    <AlertContext.Provider value={resolved}>
      <div
        data-slot="alert"
        data-layout={dir}
        data-variant={variant}
        data-size={size}
        className={cn(alertContainerVariants({ dir, variant, size }), className)}
        {...props}
      />
    </AlertContext.Provider>
  )
}

function Icon({ className, ...props }: React.ComponentProps<"div">) {
  const { size, variant } = useAlertContext("Alert.Icon")
  return (
    <div
      data-slot="alert-icon"
      className={cn(alertIconVariants({ size, variant }), className)}
      {...props}
    />
  )
}

function Title({ className, ...props }: React.ComponentProps<"div">) {
  const { size } = useAlertContext("Alert.Title")
  return (
    <div
      data-slot="alert-title"
      className={cn(alertTitleVariants({ size }), className)}
      {...props}
    />
  )
}

function Content({ className, ...props }: React.ComponentProps<"div">) {
  const { dir, size, variant } = useAlertContext("Alert.Content")
  return (
    <div
      data-slot="alert-content"
      className={cn(
        dir === "horizontal" ? "flex-1" : "w-full",
        alertContentVariants({ size, variant }),
        className,
      )}
      {...props}
    />
  )
}

const Alert = Object.assign(
  {},
  {
    Container,
    Icon,
    Title,
    Content,
  },
)

export {
  Alert,
  Container,
  Icon,
  Title,
  Content,
  alertContainerVariants,
  alertIconVariants,
  alertTitleVariants,
  alertContentVariants,
}
